import { useEffect, useRef, useState } from 'react'

const BANNER_WIDTH = 1500
const BANNER_HEIGHT = 500
const PROFILE_X = 30
const PROFILE_Y = 90
const PROFILE_SIZE = 320
const TEXT_START_X = 420
const TEXT_START_Y = 120
const TEXT_LINE_GAP = 90

const DEFAULT_BACKGROUND_IMAGE =
    'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/691c7c4227e925df7749e928/13f125fa4_UntitledProject93.jpg'
const DEFAULT_STAMP_IMAGE =
    'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/691c7c4227e925df7749e928/8d4eb8e9d_UntitledProject-2025-11-18T130757739.png'

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const readImageFromFile = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (event) => resolve(event.target?.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        if (!src) {
            reject(new Error('Image source is empty.'))
            return
        }
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => resolve(image)
        image.onerror = reject
        image.src = src
    })

export default function BannerStudio() {
    const canvasRef = useRef(null)
    const profileInputRef = useRef(null)
    const [profileImage, setProfileImage] = useState(null)
    const [name, setName] = useState('')
    const [occupation, setOccupation] = useState('')
    const [colour, setColour] = useState('')
    const [signature, setSignature] = useState('')
    const [stampColor, setStampColor] = useState('#4a7c8c')
    const [stampX, setStampX] = useState(1200)
    const [stampY, setStampY] = useState(50)
    const [stampRotation, setStampRotation] = useState(15)
    const [stampSize, setStampSize] = useState(250)
    const [isDraggingStamp, setIsDraggingStamp] = useState(false)
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

    useEffect(() => {
        const drawBanner = async () => {
            const canvas = canvasRef.current
            if (!canvas) return
            const context = canvas.getContext('2d')
            if (!context) return

            const ratio = window.devicePixelRatio || 1
            canvas.width = BANNER_WIDTH * ratio
            canvas.height = BANNER_HEIGHT * ratio
            context.setTransform(1, 0, 0, 1, 0, 0)
            context.scale(ratio, ratio)
            context.clearRect(0, 0, BANNER_WIDTH, BANNER_HEIGHT)

            try {
                const bg = await loadImage(DEFAULT_BACKGROUND_IMAGE)
                context.drawImage(bg, 0, 0, BANNER_WIDTH, BANNER_HEIGHT)
            } catch {
                context.fillStyle = '#1a1a2e'
                context.fillRect(0, 0, BANNER_WIDTH, BANNER_HEIGHT)
            }

            if (profileImage) {
                try {
                    const profile = await loadImage(profileImage)
                    context.save()
                    context.beginPath()
                    context.arc(PROFILE_X + PROFILE_SIZE / 2, PROFILE_Y + PROFILE_SIZE / 2, PROFILE_SIZE / 2, 0, Math.PI * 2)
                    context.closePath()
                    context.clip()
                    context.drawImage(profile, PROFILE_X, PROFILE_Y, PROFILE_SIZE, PROFILE_SIZE)
                    context.restore()
                    context.strokeStyle = 'rgba(255, 255, 255, 0.2)'
                    context.lineWidth = 4
                    context.beginPath()
                    context.arc(PROFILE_X + PROFILE_SIZE / 2, PROFILE_Y + PROFILE_SIZE / 2, PROFILE_SIZE / 2, 0, Math.PI * 2)
                    context.stroke()
                } catch {
                    // If image fails, show placeholder circle.
                }
            } else {
                context.fillStyle = 'rgba(255, 255, 255, 0.05)'
                context.beginPath()
                context.arc(PROFILE_X + PROFILE_SIZE / 2, PROFILE_Y + PROFILE_SIZE / 2, PROFILE_SIZE / 2, 0, Math.PI * 2)
                context.fill()
                context.strokeStyle = 'rgba(255, 255, 255, 0.15)'
                context.lineWidth = 3
                context.setLineDash([10, 10])
                context.beginPath()
                context.arc(PROFILE_X + PROFILE_SIZE / 2, PROFILE_Y + PROFILE_SIZE / 2, PROFILE_SIZE / 2, 0, Math.PI * 2)
                context.stroke()
                context.setLineDash([])
            }

            let textY = TEXT_START_Y
            const drawLabelValue = (label, value, valueFont = '600 42px Georgia, serif', yAdjust = 0) => {
                if (!value) return
                context.fillStyle = '#000000'
                context.font = 'bold 42px Georgia, serif'
                context.fillText(label, TEXT_START_X, textY)
                const labelWidth = context.measureText(label).width
                context.font = valueFont
                context.fillText(value, TEXT_START_X + labelWidth + 30, textY + yAdjust)
                textY += TEXT_LINE_GAP
            }

            context.textBaseline = 'top'
            drawLabelValue('NAME:', name)
            drawLabelValue('COLOUR:', colour)
            drawLabelValue('OCCUPATION:', occupation)
            drawLabelValue('SIGNATURE:', signature, 'italic 64px Brush Script MT, cursive', -12)

            try {
                const stampSource = await loadImage(DEFAULT_STAMP_IMAGE)
                context.save()
                context.translate(stampX, stampY)
                context.rotate((stampRotation * Math.PI) / 180)
                const tintCanvas = document.createElement('canvas')
                tintCanvas.width = stampSize
                tintCanvas.height = stampSize
                const tintCtx = tintCanvas.getContext('2d')
                if (tintCtx) {
                    tintCtx.drawImage(stampSource, 0, 0, stampSize, stampSize)
                    tintCtx.globalCompositeOperation = 'multiply'
                    tintCtx.fillStyle = stampColor
                    tintCtx.fillRect(0, 0, stampSize, stampSize)
                    tintCtx.globalCompositeOperation = 'destination-in'
                    tintCtx.drawImage(stampSource, 0, 0, stampSize, stampSize)
                    context.drawImage(tintCanvas, -stampSize / 2, -stampSize / 2)
                }
                context.restore()
            } catch {
                // Stamp is decorative; do not block rendering if unavailable.
            }
        }

        drawBanner()
    }, [profileImage, name, occupation, colour, signature, stampColor, stampX, stampY, stampRotation, stampSize])

    const handleProfileUpload = async (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        if (!file.type.startsWith('image/')) {
            window.alert('Please upload an image file.')
            return
        }
        try {
            const imageData = await readImageFromFile(file)
            setProfileImage(imageData)
        } catch {
            window.alert('Unable to read the selected profile image.')
        } finally {
            event.target.value = ''
        }
    }

    const handleStampMouseDown = (event) => {
        const canvas = canvasRef.current
        if (!canvas) return
        const rect = canvas.getBoundingClientRect()
        const xScale = BANNER_WIDTH / rect.width
        const yScale = BANNER_HEIGHT / rect.height
        const pointerX = (event.clientX - rect.left) * xScale
        const pointerY = (event.clientY - rect.top) * yScale
        const dx = pointerX - stampX
        const dy = pointerY - stampY
        if (Math.sqrt(dx * dx + dy * dy) > stampSize / 2) return
        setIsDraggingStamp(true)
        setDragOffset({ x: dx, y: dy })
    }

    const handleStampMouseMove = (event) => {
        if (!isDraggingStamp) return
        const canvas = canvasRef.current
        if (!canvas) return
        const rect = canvas.getBoundingClientRect()
        const xScale = BANNER_WIDTH / rect.width
        const yScale = BANNER_HEIGHT / rect.height
        const pointerX = (event.clientX - rect.left) * xScale
        const pointerY = (event.clientY - rect.top) * yScale
        setStampX(clamp(pointerX - dragOffset.x, 0, BANNER_WIDTH))
        setStampY(clamp(pointerY - dragOffset.y, 0, BANNER_HEIGHT))
    }

    const exportBanner = () => {
        const canvas = canvasRef.current
        if (!canvas) return
        const link = document.createElement('a')
        link.download = `dawae-banner-${Date.now()}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    }

    return (
        <section className="py-10 text-ugandan-white">
            <div className="container mx-auto px-4 space-y-5">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-ugandan-red">Banner Studio</h1>
                    <p className="text-sm text-gray-300 mt-2">Create a custom banner with profile, details, and stamp controls.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 border border-ugandan-red/30 rounded-lg bg-black/30 p-3">
                        <canvas
                            ref={canvasRef}
                            className="w-full h-auto rounded border border-ugandan-red/40 cursor-grab active:cursor-grabbing"
                            onMouseDown={handleStampMouseDown}
                            onMouseMove={handleStampMouseMove}
                            onMouseUp={() => setIsDraggingStamp(false)}
                            onMouseLeave={() => setIsDraggingStamp(false)}
                        />
                        <div className="mt-3 flex justify-end">
                            <button type="button" onClick={exportBanner} className="bg-ugandan-red hover:bg-ugandan-red/80 text-ugandan-white px-4 py-2 rounded text-xs font-semibold">
                                Export Banner PNG
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="border border-ugandan-red/30 rounded-lg bg-black/30 p-3 space-y-2">
                            <p className="text-xs font-semibold text-ugandan-red uppercase">Profile Image</p>
                            <input ref={profileInputRef} type="file" accept="image/*" onChange={handleProfileUpload} className="block w-full text-xs" />
                        </div>

                        <div className="border border-ugandan-red/30 rounded-lg bg-black/30 p-3 space-y-2">
                            <p className="text-xs font-semibold text-ugandan-red uppercase">Details</p>
                            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Name" className="w-full bg-black border border-white/30 rounded px-2 py-2 text-xs" />
                            <input value={occupation} onChange={(event) => setOccupation(event.target.value)} placeholder="Occupation" className="w-full bg-black border border-white/30 rounded px-2 py-2 text-xs" />
                            <input value={colour} onChange={(event) => setColour(event.target.value)} placeholder="Colour" className="w-full bg-black border border-white/30 rounded px-2 py-2 text-xs" />
                            <input value={signature} onChange={(event) => setSignature(event.target.value)} placeholder="Signature" className="w-full bg-black border border-white/30 rounded px-2 py-2 text-xs italic" />
                        </div>

                        <div className="border border-ugandan-red/30 rounded-lg bg-black/30 p-3 space-y-2">
                            <p className="text-xs font-semibold text-ugandan-red uppercase">Stamp Controls</p>
                            <label className="block text-xs">Stamp Color</label>
                            <input type="color" value={stampColor} onChange={(event) => setStampColor(event.target.value)} className="w-full h-10 rounded border border-white/30" />
                            <label className="block text-xs">Position X: {Math.round(stampX)}</label>
                            <input type="range" min={0} max={BANNER_WIDTH} step={10} value={stampX} onChange={(event) => setStampX(Number(event.target.value))} className="w-full" />
                            <label className="block text-xs">Position Y: {Math.round(stampY)}</label>
                            <input type="range" min={0} max={BANNER_HEIGHT} step={10} value={stampY} onChange={(event) => setStampY(Number(event.target.value))} className="w-full" />
                            <label className="block text-xs">Rotation: {Math.round(stampRotation)}deg</label>
                            <input type="range" min={-180} max={180} step={5} value={stampRotation} onChange={(event) => setStampRotation(Number(event.target.value))} className="w-full" />
                            <label className="block text-xs">Size: {Math.round(stampSize)}px</label>
                            <input type="range" min={50} max={400} step={10} value={stampSize} onChange={(event) => setStampSize(Number(event.target.value))} className="w-full" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
